/**
 * Gantt date helpers — work in whole UTC day-numbers (days since epoch) to avoid
 * timezone/DST drift. Dates are `YYYY-MM-DD` strings throughout.
 */

const MS_DAY = 86400000

/** `YYYY-MM-DD` -> integer day number (days since 1970-01-01 UTC). */
export function isoToDayNum(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number)
  return Math.round(Date.UTC(y, (m || 1) - 1, d || 1) / MS_DAY)
}

/** integer day number -> `YYYY-MM-DD`. */
export function dayNumToISO(n: number): string {
  const d = new Date(n * MS_DAY)
  const pad = (x: number) => String(x).padStart(2, '0')
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
}

export function addDaysISO(iso: string, days: number): string {
  return dayNumToISO(isoToDayNum(iso) + days)
}

/** Whole days from `a` to `b` (b - a). */
export function daysBetweenISO(a: string, b: string): number {
  return isoToDayNum(b) - isoToDayNum(a)
}

/** Parse a Gantt duration token (`7d`, `2w`, `3` → days). Returns 0 if unparseable. */
export function durationToDays(token: string): number {
  const m = token.trim().match(/^(\d+(?:\.\d+)?)\s*([dwhm]?)$/i)
  if (!m) return 0
  const n = parseFloat(m[1])
  switch ((m[2] || 'd').toLowerCase()) {
    case 'w': return Math.round(n * 7)
    case 'h': return Math.max(0, Math.round(n / 24))
    case 'm': return 0
    default: return Math.round(n)
  }
}

export function isISODate(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(s.trim())
}

/** Short tick label for the axis, e.g. "Jan 8". */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export function tickLabel(iso: string): string {
  const [, m, d] = iso.split('-').map(Number)
  return `${MONTHS[(m || 1) - 1]} ${d}`
}
